import type { UniformItemRow, UniformRequestRow, UniformStatus } from '@routeback/core';

/** Driver Portal wording: Pending → Ordered → Arrived → Handed out. */
export const STATUS_LABEL: Record<UniformStatus, string> = {
  requested: 'Requested',
  ordered: 'Ordered',
  ready: 'Ready to pick up',
  delivered: 'Handed out',
  declined: 'Declined',
  cancelled: 'Cancelled',
};
export const OPEN_STATUSES: readonly UniformStatus[] = ['requested', 'ordered', 'ready'];

export interface Line { itemId: string; size: string; qty: number }

/** What's wrong with the request, or null when it can be sent (same rules as the database). */
export function validateLines(lines: Line[], items: UniformItemRow[]): string | null {
  const wanted = lines.filter((l) => l.qty > 0);
  if (!wanted.length) return 'Pick at least one item.';
  for (const l of wanted) {
    const it = items.find((i) => i.id === l.itemId);
    if (!it) return 'That item is no longer offered.';
    if (!l.size.trim()) return `Pick a size for ${it.name.toLowerCase()}.`;
    if (it.sizes.length && !it.sizes.includes(l.size)) return `${it.name} comes in ${it.sizes.join(', ')}.`;
    if (l.qty > it.max_qty) return `Up to ${it.max_qty} ${it.name.toLowerCase()}${it.max_qty === 1 ? '' : 's'}.`;
  }
  return null;
}

export interface Batch { id: string; at: string; note: string; lines: UniformRequestRow[] }

/** One card per request sent (rows sharing batch_id; old rows without one stand alone), newest first. */
export function batches(rows: UniformRequestRow[]): Batch[] {
  const map = new Map<string, Batch>();
  for (const r of rows) {
    if (r.deleted_at) continue;
    const key = r.batch_id ?? r.id;
    const b = map.get(key) ?? { id: key, at: r.created_at, note: '', lines: [] };
    b.lines.push(r);
    if (r.created_at < b.at) b.at = r.created_at;
    if (r.note && !b.note) b.note = r.note;
    map.set(key, b);
  }
  return [...map.values()].sort((a, b) => b.at.localeCompare(a.at));
}

/** A batch is open while any item is still on its way. */
export const batchOpen = (b: Batch): boolean => b.lines.some((l) => OPEN_STATUSES.includes(l.status));

/** The batch's headline status: the least-advanced open item, else the last state. */
export function batchStatus(b: Batch): UniformStatus {
  for (const s of ['requested', 'ordered', 'ready'] as UniformStatus[]) if (b.lines.some((l) => l.status === s)) return s;
  if (b.lines.some((l) => l.status === 'delivered')) return 'delivered';
  if (b.lines.some((l) => l.status === 'declined')) return 'declined';
  return 'cancelled';
}
